"use client";

import React from "react";
import LinkedIn from "@/app/parts/tracking/LinkedIn";
import Accessible from "@/app/parts/tracking/Accessible";
import LiveChat from "@/app/parts/tracking/LiveChat";
import Clarity from "@/app/parts/tracking/Clarity";
import Hotjar from '@/app/parts/tracking/Hotjar';
import GoogleTagHeaderManager from '@/app/parts/tracking/GoogleTagHeaderManager';
import HubSpot from '@/app/parts/tracking/HubSpot';

const CONSENT_COOKIE_NAME = 'zaraz-consent-preferences';
const EU_COOKIE_NAME = 'cf-is-eu';

// Helper to get cookie value
const getCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
};

// Check if user is from EU
const checkIsEU = (): boolean | null => {
    const euCookie = getCookie(EU_COOKIE_NAME);
    if (euCookie === null) return null;
    return euCookie === '1';
};

// Check if user has given consent
const checkHasConsent = (): boolean => {
    const consentCookie = getCookie(CONSENT_COOKIE_NAME);
    if (!consentCookie) return false;
    return consentCookie === 'all' || consentCookie === 'auto-granted';
};

export const ConsentAwareTracking = (): React.ReactElement | null => {
    const [shouldLoadTracking, setShouldLoadTracking] = React.useState(false);
    const [isInitialized, setIsInitialized] = React.useState(false);

    React.useEffect(() => {
        const determineTrackingStatus = () => {
            const isEU = checkIsEU();

            // If not from EU, load tracking immediately
            if (isEU === false) {
                setShouldLoadTracking(true);
                setIsInitialized(true);
                return;
            }

            // If from EU, check consent status
            if (isEU === true) {
                const hasConsent = checkHasConsent();
                setShouldLoadTracking(hasConsent);
                setIsInitialized(true);
                return;
            }

            // If EU status unknown, wait and retry
            // This handles the case where the cookie hasn't been set yet
        };

        // Initial check
        determineTrackingStatus();

        // If not initialized, set up retry logic
        if (!isInitialized) {
            let attempts = 0;
            const interval = setInterval(() => {
                attempts++;
                const isEU = checkIsEU();
                if (isEU !== null || attempts >= 5) {
                    clearInterval(interval);
                    determineTrackingStatus();
                }
            }, 500);

            return () => clearInterval(interval);
        }
    }, [isInitialized]);

    // Listen for consent updates
    React.useEffect(() => {
        const handleConsentUpdate = (event: Event) => {
            const customEvent = event as CustomEvent<{ hasConsent: boolean; isEU: boolean }>;
            const { hasConsent, isEU } = customEvent.detail;

            // For non-EU users, always load tracking
            if (!isEU) {
                setShouldLoadTracking(true);
                return;
            }

            // For EU users, respect consent choice
            setShouldLoadTracking(hasConsent);
        };

        window.addEventListener('cookieConsentUpdated', handleConsentUpdate);

        return () => {
            window.removeEventListener('cookieConsentUpdated', handleConsentUpdate);
        };
    }, []);

    // Don't render tracking scripts until we've determined consent status
    if (!isInitialized) {
        return null;
    }

    // Don't load tracking for EU users without consent
    if (!shouldLoadTracking) {
        return null;
    }

    // GA4 (G-FZV6ZE9PZY) is loaded by the Google tag inside GTM-MB6CSV32, not here.
    // Adding a gtag / <GoogleAnalytics> loader back sends every page_view twice, which
    // marks every session as engaged and makes GA4's bounce rate meaningless.
    return (
        <>
            <LinkedIn />
            <Accessible />
            <LiveChat />
            <Clarity />
            <Hotjar />
            <GoogleTagHeaderManager />
            <HubSpot />
        </>
    );
};

export default ConsentAwareTracking;
